import { Flex, Spacer, Text } from "@hackthenorth/north";
import { Link, TextInput } from "north.js";
import React, { ChangeEvent, useState } from "react";
import { styled } from "twin.macro";

import DateTimePicker from "src/shared/components/DateTimePicker";
import Icon from "src/shared/components/Icon";
import Label from "src/shared/components/Label";
import { Field, TAnswer } from "src/shared/contexts/HackerContext/types";

import { VALIDATORS } from "./constants";
import { SectionHeader } from "./flightForm";
import { getFieldError } from "./validation";

type ErrorsT = Record<string, string>;

type ResponsesStateT = Partial<Record<Field, TAnswer>>;

type FlightDetailsPropsT = {
  showError: boolean;
  day: string;
  fields: {
    airport: Field;
    flightNo: Field;
    airline: Field;
    arrivalTime: Field;
  };
  errors: ErrorsT;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setResponsesState: (field: Field, value: TAnswer) => void;
  responsesState: ResponsesStateT;
  isReadOnly: boolean;
};

export const FlightDetails: React.FC<FlightDetailsPropsT> = ({
  showError,
  day,
  fields,
  errors,
  setErrors,
  setResponsesState,
  responsesState,
  isReadOnly,
}) => {
  const isArrival = day === "Friday";
  const arrivalOrDeparture = isArrival ? "arrival" : "departure";

  const timehint = isArrival ? (
    <span>
      We recommend you arrive by <b>6:00 PM EDT</b> on{" "}
      <b>Friday, September 18</b>. Our shuttles only accommodate hackers on
      flights that arrive by 6:00 PM. If your flight arrives{" "}
      <b>after 6:00 PM, you may need to arrange your own transportation</b> to
      Hack the North from the airport. If you are arriving internationally and
      find it easier to arrive shortly after 6:00 PM, please email us at{" "}
      <StyledLink href="mailto:travel@hackthenorth.com">
        travel@hackthenorth.com
      </StyledLink>
    </span>
  ) : (
    <span>
      We recommend you depart{" "}
      <b>no earlier than 8:30 PM EDT on Sunday, September 20</b>. Our shuttles
      only accommodate hackers on flights that
      <b> depart after 8:30 PM from Toronto Pearson Airport</b>. If your flight
      departs before 8:30 PM, we will provide one earlier shuttle with limited
      space, or you may need to take a taxi to the airport.
    </span>
  );

  const timeCloserTo = isArrival
    ? " 6:00 PM EDT on Friday, September 18"
    : " 8:30 PM EDT on Sunday, September 20";

  const arrivalRaw = responsesState[fields.arrivalTime] as unknown as
    | string
    | undefined;
  const arrivalDate = new Date(arrivalRaw ?? "");
  const dateError = isArrival
    ? !(
        arrivalDate >= new Date("2026-09-10T00:00:00") &&
        arrivalDate <= new Date("2026-09-19T23:59:59")
      )
    : !(
        arrivalDate >= new Date("2026-09-20T00:00:00") &&
        arrivalDate <= new Date("2026-09-25T23:59:59")
      );

  const [didEdit, setDidEdit] = useState({
    airport: false,
    flightNo: false,
    airline: false,
    time: false,
  });

  return (
    <div>
      <SectionHeader>{day} flight details</SectionHeader>
      <Spacer height={24} />
      <SectionContainer>
        <Label
          value={`${
            day === "Friday"
              ? "What airport are you flying to?"
              : "What airport are you flying from?"
          }`}
        >
          <StyledTextInput
            value={String((responsesState[fields.airport] as string) ?? "")}
            hint={"Please enter the airport code (i.e. YYZ)"}
            error={didEdit.airport || showError ? errors[fields.airport] : null}
            placeholder={"Enter your arrival airport"}
            disabled={isReadOnly}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setDidEdit({ ...didEdit, airport: true });
              const validator = VALIDATORS[fields.airport];
              if (validator !== undefined) {
                setErrors({
                  ...errors,
                  [fields.airport]: getFieldError(
                    fields.airport,
                    e.target.value
                  ),
                });
              }
              setResponsesState(fields.airport, e.target.value);
            }}
          />
        </Label>
        <Label
          value={`What is the flight number of your ${
            isArrival ? "arriving" : "departing"
          } flight?`}
        >
          <StyledTextInput
            value={String((responsesState[fields.flightNo] as string) ?? "")}
            placeholder={"Enter your flight number"}
            hint={
              "Please enter the full flight number exactly (i.e. if it looks like AC592, please enter AC592 exactly)."
            }
            disabled={isReadOnly}
            error={
              didEdit.flightNo || showError ? errors[fields.flightNo] : null
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setDidEdit({ ...didEdit, flightNo: true });
              const validator = VALIDATORS[fields.flightNo];
              if (validator !== undefined) {
                setErrors({
                  ...errors,
                  [fields.flightNo]: getFieldError(
                    fields.flightNo,
                    e.target.value
                  ),
                });
              }
              setResponsesState(fields.flightNo, e.target.value);
            }}
          />
        </Label>
        <Label value={"Which airline will you be flying with?"}>
          <StyledTextInput
            value={String((responsesState[fields.airline] as string) ?? "")}
            placeholder={"Enter the full airline name"}
            hint={"Please enter the full airline name."}
            disabled={isReadOnly}
            error={didEdit.airline || showError ? errors[fields.airline] : null}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setDidEdit({ ...didEdit, airline: true });
              const validator = VALIDATORS[fields.airline];
              if (validator !== undefined) {
                setErrors({
                  ...errors,
                  [fields.airline]: getFieldError(
                    fields.airline,
                    e.target.value
                  ),
                });
              }
              setResponsesState(fields.airline, e.target.value);
            }}
          />
        </Label>
        <Label
          value={`What is your ${arrivalOrDeparture} date and time (in EDT)?`}
          caption={timehint}
        >
          <DateTimePicker
            value={String((responsesState[fields.arrivalTime] as string) ?? "")}
            onChange={(date: string) => {
              setDidEdit({ ...didEdit, time: true });
              const validator = VALIDATORS[fields.arrivalTime];
              if (validator !== undefined) {
                setErrors({
                  ...errors,
                  [fields.arrivalTime]: getFieldError(
                    fields.arrivalTime,
                    date,
                    { arrivalOrDeparture }
                  ),
                });
                setResponsesState(
                  fields.arrivalTime,
                  date as unknown as TAnswer
                );
              }
            }}
            error={
              didEdit.time || showError ? errors[fields.arrivalTime] : null
            }
            placeholder={
              isArrival
                ? "Enter your arrival date and time"
                : "Enter your departure date and time"
            }
            disabled={isReadOnly}
          />
        </Label>
        {responsesState[fields.arrivalTime] && dateError && (
          <ChangeDateBadge timeCloserTo={timeCloserTo} />
        )}
      </SectionContainer>
    </div>
  );
};

interface ChangeDateBadgeProps {
  timeCloserTo: string;
}

const ChangeDateBadge: React.FC<ChangeDateBadgeProps> = ({ timeCloserTo }) => {
  return (
    <InfoBadge color="primary-light">
      <div style={{ width: "32px", height: "32px" }}>
        <Icon name="info" size="heading" />
      </div>
      <Text mods="big medium blue lh-regular">
        This time is too far from the event to qualify for reimbursement under
        our{" "}
        <Link href="https://hackthenorth.com/travel-guidelines">
          Travel Guidelines
        </Link>
        . Please enter a time closer to
        {timeCloserTo} if you wish to be reimbursed.
      </Text>
    </InfoBadge>
  );
};

const SectionContainer = styled(Flex).attrs({
  column: true,
  justify: "space-between",
})`
  gap: 24px;
`;

const StyledLink = styled(Link)`
  font-size: 12px !important;
  font-weight: bold;
`;

const StyledTextInput = styled(TextInput).attrs({ size: "md" })`
  width: 100%;
`;

const InfoBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  border-radius: 8px;
  width: 100%;
  justify-content: space-between;

  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
`;
